/** Limite max acceptée par GET /api/missions (backend: Query(20, ge=1, le=50)). */
export const MISSIONS_API_LIMIT = 50;

/**
 * Fenêtre de litige post-livraison (ms). Miroir de backend DISPUTE_WINDOW_HOURS = 48.
 * Partagée avec DisputeModal pour éviter la dérive entre frontend et logique de test.
 */
export const DISPUTE_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Construit le Set des entités (shipment ou trip) ayant une mission active.
 * "Active" = statut ∉ CANCELLED | DECLINED | COMPLETED.
 * @param {Array}  missions — tableau de missions retourné par /api/missions
 * @param {string} idKey   — 'shipment_id' ou 'trip_id'
 * @returns {Set<string>}
 */
export function buildActiveMissionIds(missions, idKey) {
  return new Set(
    missions
      .filter(m => !['CANCELLED', 'DECLINED', 'COMPLETED'].includes(m.status))
      .map(m => m[idKey])
      .filter(Boolean)
  );
}

/**
 * Construit le Set des trip_ids bloquant l'annulation du trajet.
 * Bloquant = IN_ESCROW | IN_TRANSIT | DELIVERED (une livraison est en cours).
 * @param {Array} missions
 * @returns {Set<string>}
 */
export function buildBlockingMissionTripIds(missions) {
  return new Set(
    missions
      .filter(m => ['IN_ESCROW', 'IN_TRANSIT', 'DELIVERED'].includes(m.status))
      .map(m => m.trip_id)
      .filter(Boolean)
  );
}

/**
 * Retourne true si l'ouverture du modal QR est autorisée pour le statut de mission.
 * Empêche la race condition où showQrCodes reste true après annulation de mission.
 * @param {string|undefined} missionStatus
 * @param {'codes'|'pickup'|'delivery'} modalType
 * @returns {boolean}
 */
export function isQrModalAllowed(missionStatus, modalType) {
  if (modalType === 'codes')    return ['IN_ESCROW', 'IN_TRANSIT', 'DELIVERED'].includes(missionStatus);
  if (modalType === 'pickup')   return missionStatus === 'IN_ESCROW';
  if (modalType === 'delivery') return missionStatus === 'IN_TRANSIT';
  return false;
}

/**
 * Retourne true si la capacité disponible du trajet est inférieure au poids du colis.
 * Retourne false si le trajet est déjà FULL (traité séparément dans l'UI).
 * @param {{ available_weight_kg: number, status: string }} trip
 * @param {number|null|undefined} shipmentWeightKg
 * @returns {boolean}
 */
export function isTripCapacityInsufficient(trip, shipmentWeightKg) {
  const isFull = trip.available_weight_kg <= 0 || trip.status === 'FULL';
  return !isFull && (shipmentWeightKg || 0) > 0 && trip.available_weight_kg < (shipmentWeightKg || 0);
}

/**
 * Retourne true si la fenêtre de litige 48h est expirée.
 * Conservateur : retourne true si confirmed_at absent (mieux vaut bloquer que laisser passer).
 * Retourne false si la mission n'est pas COMPLETED (aucune fenêtre applicable).
 * @param {{ status: string, confirmed_at: string|null }|null|undefined} mission
 * @returns {boolean}
 */
export function isDisputeWindowExpired(mission) {
  if (mission?.status !== 'COMPLETED') return false;
  if (!mission?.confirmed_at) return true;
  return Date.now() > new Date(mission.confirmed_at).getTime() + DISPUTE_WINDOW_MS;
}
